import { IPurchasable } from "@smartrr/shared/entities/Purchasable";
import { PurchaseStateAndDelivery } from "@smartrr/shared/entities/PurchaseState";
import React, { useState } from "react";

import { HandleReselectingSellingPlan } from "./components/HandleReselectingSellingPlan";
import { ModalButtonGrid } from "./components/ModalButtonGrid";
import { SubPlanDeleted } from "./components/SubPlanDeleted";
import { useBasicSubscriptionInfo } from "./hooks/useBasicSubscriptionInfo";
import { useManageSubscriptionRefs } from "./hooks/useManageSubscriptionRefs";
import { useModalButtons } from "./hooks/useModalButtons";
import { useShowModalButtons } from "./hooks/useShowModalButtons";
import { useThemeConditionals } from "./hooks/useThemeConditionals";
import { useUpdatedPurchasablesForAdding } from "./hooks/useUpdatedPurchasablesForAdding";
import { useViewComponent } from "./hooks/useViewComponent";
import { useViewInfo } from "./hooks/useViewInfo";
import { useUnavailableVariantsAndDeliveries } from "../../../../hooks/useUnavailableVariantsAndDeliveries";
import { FailedPaymentBanner } from "../../../FailedPaymentBanner";
import { ManageOutOfStockAndUnavailable } from "../../../ManageOutOfStockAndUnavailable";
import { useModalButtonOptions } from "../ManageSubscriptionsModal/hooks/useModalButtonOptions";
import { ValidViews } from "../OrderOptionsModalInner";
import { DeliveryId } from "@customer-app/state/stores/delivery/DeliveryId";
import { DeliveryAccess } from "@customer-app/state/stores/delivery";

export interface IManageSubscriptionsModalProps {
  deliveryId: DeliveryId;
  purchasablesForAdding: IPurchasable[];
  initialView?: ValidViews;
  generatePurchasables: boolean;
  initialLineItemId?: string;
}

export const ManageSubscriptionsModal = ({
  deliveryId,
  purchasablesForAdding,
  initialView = "Default",
  generatePurchasables,
  initialLineItemId,
}: IManageSubscriptionsModalProps): JSX.Element => {
  const delivery = DeliveryAccess.useDelivery(deliveryId);

  if (!delivery) {
    return <React.Fragment />;
  }

  return (
    <ManageSubscriptionsModalContent
      delivery={delivery}
      purchasablesForAdding={purchasablesForAdding}
      initialView={initialView}
      generatePurchasables={generatePurchasables}
      initialLineItemId={initialLineItemId}
    />
  );
};

interface IManageSubscriptionsModalContentProps extends Omit<IManageSubscriptionsModalProps, "deliveryId"> {
  delivery: PurchaseStateAndDelivery;
}

const ManageSubscriptionsModalContent = ({
  delivery,
  purchasablesForAdding: stalePurchasablesForAdding,
  initialView = "Default",
  generatePurchasables,
  initialLineItemId,
}: IManageSubscriptionsModalContentProps): JSX.Element => {
  const { sellingPlanOptions, isSellingPlanDeleted, purchaseState } = useBasicSubscriptionInfo(delivery);

  const purchasablesForAdding = useUpdatedPurchasablesForAdding(
    stalePurchasablesForAdding,
    delivery,
    generatePurchasables
  );

  const viewInfo = useViewInfo(initialView);

  const [areButtonsDisabled, setButtonsDisabled] = useState(false);

  const { areThemeConditionalActions } = useThemeConditionals();

  const buttonOptions = useModalButtonOptions(delivery, "manageSubscription");
  const modalRefs = useManageSubscriptionRefs();
  const { setIconButtonActive, setIconButtonInactive } = useModalButtons(
    delivery,
    viewInfo,
    modalRefs,
    buttonOptions
  );

  const { shouldBannersBeShown, isDeliveryUnavailable } = useUnavailableVariantsAndDeliveries(purchaseState.id);

  const showBanner = shouldBannersBeShown && isDeliveryUnavailable;

  const showButtons = useShowModalButtons(delivery, viewInfo, !!areThemeConditionalActions);

  const viewComponent = useViewComponent(
    viewInfo,
    delivery,
    purchasablesForAdding,
    setButtonsDisabled,
    setIconButtonInactive,
    initialLineItemId
  );

  const showSubscriptionplanDeleted = isSellingPlanDeleted && viewInfo.view !== "Swap Plan";

  return (
    <React.Fragment>
      <React.Fragment>
        {showButtons ? (
          <ModalButtonGrid
            modalRefs={modalRefs}
            buttonOptions={buttonOptions}
            areButtonsDisabled={areButtonsDisabled}
            setIconButtonActive={setIconButtonActive}
            view={viewInfo.view}
          />
        ) : (
          <React.Fragment />
        )}
        {showSubscriptionplanDeleted ? <SubPlanDeleted /> : <React.Fragment />}
        {viewInfo.view === "Default" ? (
          <React.Fragment>
            {showBanner ? <ManageOutOfStockAndUnavailable targetDelivery={delivery} /> : <React.Fragment />}
            {purchaseState ? <FailedPaymentBanner purchaseState={purchaseState} /> : <React.Fragment />}
          </React.Fragment>
        ) : (
          <React.Fragment />
        )}

        {viewComponent}
        {showSubscriptionplanDeleted ? (
          <HandleReselectingSellingPlan
            sellingPlanOptions={sellingPlanOptions}
            delivery={delivery}
            setView={viewInfo.set}
          />
        ) : (
          <React.Fragment />
        )}
      </React.Fragment>
    </React.Fragment>
  );
};
